import { Chip } from "@/components/Chip";
import { PageHead } from "@/components/PageHead";
import { useProductPicker } from "@/modules/products/ProductPicker";
import { REF_TYPE_DESC, REF_TYPE_LABEL, useProductRefFiles } from "@/modules/products/fixtures";
import { SoonButton } from "@/components/SoonButton";

const SLOTS = ["app_config_did", "mfg_config_did", "data_upload_signals"] as const;

/** Reference files — the three fixed slots per product.
 *
 *  These tell the tool how to ADDRESS AND DECODE an ECU: which DIDs exist, what
 *  the signal maps mean, how to read a data log. They never land on a board,
 *  which is why they stay with the system the tool logs into rather than moving
 *  to the registry that tracks units.
 *
 *  A company may override a slot; the resolver takes the override first and
 *  falls back to the default shown here.
 */
export function ReferenceFilesPage() {
  const { product, picker } = useProductPicker();
  const refs = useProductRefFiles(product.product_id);

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Reference files"
        title="Reference files"
        desc="Three fixed slots per product. They tell the tool how to address and decode an ECU — they never land on a board."
        right={picker}
      />

      {SLOTS.map((rt) => {
        const f = refs.find((r) => r.ref_type === rt);
        return (
          <div key={rt} className="panel panel-pad" style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{REF_TYPE_LABEL[rt]}</span>
              {f && f.overrides > 0 && (
                <Chip tone="warn">{f.overrides} company override{f.overrides > 1 ? "s" : ""}</Chip>
              )}
              <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                {f && <SoonButton className="btn ghost sm" why="fetches the stored file">Download</SoonButton>}
                <SoonButton className="btn ghost sm" why="adds a version and supersedes the current one">Upload new version</SoonButton>
                {f && <SoonButton className="btn ghost sm" why="hides a version from the resolver; revivable">Archive</SoonButton>}
              </span>
            </div>
            <div className="cap" style={{ marginTop: 4 }}>{REF_TYPE_DESC[rt]}</div>
            <div style={{ marginTop: 8, fontSize: 12.5 }}>
              {f
                ? <span className="mono">{f.filename}</span>
                : <span className="cap">Nothing uploaded — the tool has no map for this slot.</span>}
              {f && (
                <span className="cap">
                  {" "}· {(f.size_bytes / 1024).toFixed(0)} KB · {f.uploaded_at} · {f.uploaded_by}
                </span>
              )}
            </div>
          </div>
        );
      })}

      <div className="cap">
        Archiving hides a version from the tool by construction — the resolver only ever sees unarchived rows —
        and it can be revived. Permanent deletion is possible only once a row is archived.
      </div>
    </div>
  );
}
